import React, { useRef, useEffect, useState } from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";

interface TruncateOnOverflowProps {
  value: string | number;
  className?: string;
  width?: string | number;
}

const TruncateOnOverflow: React.FC<TruncateOnOverflowProps> = React.memo(
  ({ value, className = "", width }) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (spanRef.current) {
          const isOverflowing = spanRef.current.scrollWidth > spanRef.current.clientWidth;
          setIsTruncated(isOverflowing);
        }
      };

      checkTruncation();
      // Check truncation when width changes
      const resizeObserver = new ResizeObserver(checkTruncation);
      if (spanRef.current) {
        resizeObserver.observe(spanRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [value, width]);

    if (!value) {
      return <span className={className}>-</span>;
    }

    const stringValue = String(value);

    const style = {
      display: "inline-block" as const,
      overflow: "hidden" as const,
      whiteSpace: "nowrap" as const,
      textOverflow: "ellipsis" as const,
      verticalAlign: "bottom" as const,
      width: width || "100%",
      maxWidth: width || "100%",
    };

    const spanElement = (
      <span ref={spanRef} className={className} style={style}>
        {stringValue}
      </span>
    );

    return isTruncated ? (
      <Tippy content={stringValue} animation="fade" theme="light">
        {spanElement}
      </Tippy>
    ) : (
      spanElement
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if value, className, or width changes
    return prevProps.value === nextProps.value && prevProps.className === nextProps.className && prevProps.width === nextProps.width;
  }
);

TruncateOnOverflow.displayName = "TruncateOnOverflow";

export default TruncateOnOverflow;
